import { useEffect, useMemo, useState } from "react";

import {
  MANAGED_KEYCLOAK_ROLES,
  deleteKeycloakUser,
  diagnoseKeycloakAdminAccess,
  fetchKeycloakTeams,
  fetchKeycloakUsersForManagement,
  saveKeycloakUser,
} from "./api";

import Swal from "sweetalert2";

const ROLE_OPTIONS = MANAGED_KEYCLOAK_ROLES;
const STATUS_OPTIONS = ["Active", "Disabled"];

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: [],
  team: [],
  status: "Active",
};

function normalizeArray(value) {
  return Array.isArray(value) ? value : value ? [value] : [];
}

export default function UsersView() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState("");
  const [editingEmail, setEditingEmail] = useState("");
  const [editingUserId, setEditingUserId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const selectedRoles = form.role;
  const selectedTeams = form.team;

  const roleDisabledState = useMemo(() => {
    const processorChecked = selectedRoles.includes("Processor");
    const checkerChecked = selectedRoles.includes("Checker");
    const restrictToSelected = selectedTeams.length > 1;

    return ROLE_OPTIONS.reduce((accumulator, role) => {
      const isSelected = selectedRoles.includes(role);
      accumulator[role] =
        (!isSelected && processorChecked && role === "Checker") ||
        (!isSelected && checkerChecked && role === "Processor") ||
        (restrictToSelected && !isSelected);
      return accumulator;
    }, {});
  }, [selectedRoles, selectedTeams]);

  const teamDisabledState = useMemo(() => {
    const restrictToOneTeam = selectedRoles.length > 1;
    return teams.reduce((accumulator, team) => {
      const isSelected = selectedTeams.includes(team.team);
      accumulator[team.team] = restrictToOneTeam && !isSelected && selectedTeams.length >= 1;
      return accumulator;
    }, {});
  }, [selectedRoles, selectedTeams, teams]);

  async function loadUsersAndTeams() {
    setLoading(true);
    setError("");

    try {
      const adminAccess = await diagnoseKeycloakAdminAccess();
      if (!adminAccess.ok) {
        throw new Error(adminAccess.message);
      }

      const [usersPayload, teamsPayload] = await Promise.all([
        fetchKeycloakUsersForManagement(),
        fetchKeycloakTeams(),
      ]);

      setUsers((usersPayload || []).map((user) => ({
        ...user,
        role: normalizeArray(user.role),
        team: normalizeArray(user.team),
      })));
      setTeams((teamsPayload || []).map((team) => ({
        ...team,
        team: team.team || team.name || "",
      })));
    } catch (requestError) {
      setError(requestError.message || "Unable to load user management data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsersAndTeams();
  }, []);

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function toggleRole(role) {
    setForm((current) => {
      const nextRoles = current.role.includes(role)
        ? current.role.filter((item) => item !== role)
        : [...current.role, role];
      return {
        ...current,
        role: nextRoles,
      };
    });
  }

  function toggleTeam(teamCode) {
    setForm((current) => {
      const nextTeams = current.team.includes(teamCode)
        ? current.team.filter((item) => item !== teamCode)
        : [...current.team, teamCode];
      return {
        ...current,
        team: nextTeams,
      };
    });
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingEmail("");
    setEditingUserId("");
  }

  function handleEdit(user) {
    setStatus("");
    setError("");
    setEditingEmail(user.email);
    setEditingUserId(user.id || "");
    setForm({
      name: user.first_name || "",
      email: user.email || "",
      password: "",
      role: normalizeArray(user.role),
      team: normalizeArray(user.team),
      status: user.status || "Active",
    });
  }

  async function handleDelete(user) {
    if (!user?.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to delete user: missing user id.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete User?",
      text: `Are you sure you want to delete ${user.email}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setDeletingUserId(user.id);
    setError("");

    try {
      await deleteKeycloakUser(user.id);

      if (editingUserId === user.id) {
        resetForm();
      }

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "User deleted successfully.",
        confirmButtonText: "OK",
      });

      await loadUsersAndTeams();
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: requestError.message || "Unable to delete user.",
        confirmButtonText: "OK",
      });

      setError(requestError.message || "Unable to delete user.");
    } finally {
      setDeletingUserId("");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      await saveKeycloakUser({
        id: editingUserId,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        team: form.team,
        status: form.status,
      });

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: editingEmail
          ? "User updated successfully."
          : "User created successfully.",
        confirmButtonText: "OK",
      });

      resetForm();
      await loadUsersAndTeams();
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: requestError.message || "Unable to save user.",
        confirmButtonText: "OK",
      });

      setError(requestError.message || "Unable to save user.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="users-view">
      <div className="workspace-panel users-view__hero">
        <div>
          <p className="eyebrow">Users</p>
          <h2>User Management</h2>
          <p className="config-view__intro">
            Manage users directly in Keycloak from this dashboard, including roles and team-group assignments.
          </p>
        </div>
        <div className="users-view__summary">
          <span className="users-view__summary-count">{users.length}</span>
          <span className="users-view__summary-label">users configured</span>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="users-layout">
        <form className="workspace-panel users-form" onSubmit={handleSubmit}>
          <div className="users-form__header">
            <div>
              <p className="eyebrow">{editingEmail ? "Edit User" : "Create User"}</p>
              <h2>{editingEmail ? "Update an existing user" : "Add a new user"}</h2>
            </div>
            {editingEmail ? (
              <button className="ghost-button" type="button" onClick={resetForm}>
                Cancel Edit
              </button>
            ) : null}
          </div>

          <label className="config-field">
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              required
            />
          </label>

          <label className="config-field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              disabled={Boolean(editingEmail)}
              required
            />
          </label>

          <label className="config-field">
            <span>Password {editingEmail ? "(optional for update)" : ""}</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => updateField("password", event.target.value)}
              placeholder={editingEmail ? "Leave blank to keep current password" : ""}
              required={!editingEmail}
            />
          </label>

          <div className="config-field">
            <span>Roles</span>
            <div className="selection-grid">
              {ROLE_OPTIONS.map((role) => (
                <label key={role} className={`selection-chip${form.role.includes(role) ? " is-selected" : ""}`}>
                  <input
                    type="checkbox"
                    value={role}
                    checked={form.role.includes(role)}
                    disabled={roleDisabledState[role]}
                    onChange={() => toggleRole(role)}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="config-field">
            <span>Teams</span>
            <div className="selection-grid">
              {teams.length ? (
                teams.map((team) => (
                  <label
                    key={team.team}
                    className={`selection-chip${form.team.includes(team.team) ? " is-selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      value={team.team}
                      checked={form.team.includes(team.team)}
                      disabled={teamDisabledState[team.team]}
                      onChange={() => toggleTeam(team.team)}
                    />
                    <span>{team.team_name || team.team}</span>
                  </label>
                ))
              ) : (
                <span className="empty-state">Create teams (groups) first to assign users.</span>
              )}
            </div>
          </div>

          <label className="config-field">
            <span>Status</span>
            <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
              {STATUS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <button className="primary-button" type="submit" disabled={saving}>
            {saving ? "Saving..." : editingEmail ? "Update User" : "Add User"}
          </button>
        </form>

        <div className="workspace-panel">
          <div className="users-table__header">
            <div>
              <p className="eyebrow">Directory</p>
              <h2>All Users</h2>
            </div>
          </div>

          {loading ? (
            <p className="config-note">Loading users…</p>
          ) : (
            <div className="config-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Teams</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length ? (
                    users.map((user) => (
                      <tr key={user.id || user.email}>
                        <td>{user.first_name}</td>
                        <td>{user.email}</td>
                        <td>{normalizeArray(user.role).join(", ") || "-"}</td>
                        <td>
                          {normalizeArray(user.team)
                            .map((teamCode) => teams.find((team) => team.team === teamCode)?.team_name || teamCode)
                            .join(", ") || "-"}
                        </td>
                        <td>{user.status || "-"}</td>
                        <td>
                          <button className="ghost-button" type="button" onClick={() => handleEdit(user)}>
                            Edit
                          </button>
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => handleDelete(user)}
                            disabled={deletingUserId === user.id}
                          >
                            {deletingUserId === user.id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
